export type NewsArticle = {
  slug: string
  title: string
  date: string
  teaser?: string
  hero?: string
  /** Folder of the article's file relative to `src/News/`, '' for top-level files; image paths resolve against it. */
  dir: string
  body: string
}

const FRONT_MATTER = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/

/** Flat `key: value` YAML front matter only; nested YAML is not supported. */
export const parseFrontMatter = (raw: string): { data: Record<string, string>; body: string } => {
  const match = FRONT_MATTER.exec(raw)
  if (!match) {
    return { data: {}, body: raw }
  }
  const data: Record<string, string> = {}
  for (const line of match[1].split(/\r?\n/)) {
    const separator = line.indexOf(':')
    if (separator <= 0 || line.trimStart().startsWith('#')) {
      continue
    }
    const key = line.slice(0, separator).trim()
    const value = line
      .slice(separator + 1)
      .trim()
      .replace(/^(["'])(.*)\1$/, '$2')
    data[key] = value
  }
  return { data, body: raw.slice(match[0].length) }
}

const dirOf = (path: string) => (path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '')

export const parseNewsArticle = (path: string, raw: string): NewsArticle | undefined => {
  const { data, body } = parseFrontMatter(raw)
  if (!data.slug || !data.title || !data.date) {
    return undefined
  }
  return { slug: data.slug, title: data.title, date: data.date, teaser: data.teaser, hero: data.hero, dir: dirOf(path), body }
}

/** Newest first. Dates are ISO strings, so placeholder dates like `2026-11-XX` still sort into their month. */
export const sortNewsArticles = (articles: NewsArticle[]): NewsArticle[] =>
  [...articles].sort((a, b) => b.date.localeCompare(a.date) || a.slug.localeCompare(b.slug))

export const resolveNewsPath = (dir: string, src: string): string => {
  const parts = dir ? dir.split('/') : []
  for (const segment of src.split('/')) {
    if (segment === '..') {
      parts.pop()
    } else if (segment !== '.' && segment !== '') {
      parts.push(segment)
    }
  }
  return parts.join('/')
}

/** Strips a glob key down to the path relative to the `News/` folder. */
export const newsRelativePath = (globKey: string): string => globKey.replace(/^.*?\/News\//, '')

export const formatNewsDate = (date: string): string => {
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(date) ? new Date(`${date}T00:00:00Z`) : undefined
  return parsed && !isNaN(parsed.getTime())
    ? parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
    : date
}
